/**
 * NotificationBell — header bell + dropdown for the user's notification center.
 *
 * Reads the `Notification` rows produced by the chat fan-out worker via
 * `notificationsApi`. The red dot reflects the real unread count (no fake
 * always-on indicator). Clicking a notification marks it read and jumps to
 * BuzzTalk.
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Bell, Check } from 'lucide-react';
import { notificationsApi, type NotificationDTO } from '../../services/api/notificationsApi';

const POLL_MS = 60_000;

/**
 * Where a notification takes you.
 *
 * Handoff alerts ("a customer needs a human") land in the inbox, where the
 * paused conversation lives. A lead notification lands on the lead: the
 * merchant was told someone arrived, and "go find them in a list" is not being
 * told. `metadata.customerId` is written by assignAndNotifyNewLead; a row
 * without one (or a chat notification) keeps going to BuzzTalk.
 */
function destinationFor(n: NotificationDTO): string {
  if (n.type === 'HANDOFF') return '/inbox';
  if (n.type === 'LEAD_ASSIGNED') {
    const customerId = n.metadata?.customerId;
    return typeof customerId === 'string' && customerId !== ''
      ? `/business/customers/${encodeURIComponent(customerId)}`
      : '/business/customers';
  }
  return '/chat';
}

export function NotificationBell() {
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [items, setItems] = useState<NotificationDTO[]>([]);
  const [unread, setUnread] = useState(0);
  const [loading, setLoading] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await notificationsApi.list(20);
      setItems(res.data?.items ?? []);
      setUnread(res.data?.unreadCount ?? 0);
    } catch {
      /* keep last good state on failure */
    } finally {
      setLoading(false);
    }
  }, []);

  // Initial load + light poll so the badge stays roughly current.
  useEffect(() => {
    let active = true;
    void load();
    const id = window.setInterval(() => {
      if (active) void load();
    }, POLL_MS);
    return () => {
      active = false;
      window.clearInterval(id);
    };
  }, [load]);

  // Close on outside click.
  useEffect(() => {
    const onDown = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', onDown);
    return () => document.removeEventListener('mousedown', onDown);
  }, []);

  const toggle = () => {
    const next = !open;
    setOpen(next);
    if (next) void load();
  };

  const markAll = async () => {
    await notificationsApi.markRead().catch(() => undefined);
    setItems((prev) => prev.map((n) => ({ ...n, read: true })));
    setUnread(0);
  };

  const openItem = async (n: NotificationDTO) => {
    setOpen(false);
    if (!n.read) {
      await notificationsApi.markRead([n.id]).catch(() => undefined);
      setUnread((u) => Math.max(0, u - 1));
    }
    navigate(destinationFor(n));
  };

  return (
    <div className="relative" ref={ref}>
      <button
        onClick={toggle}
        className="relative p-2 hover:bg-gray-100 rounded-lg transition-colors"
        aria-label="Notifications"
      >
        <Bell className="w-5 h-5 text-gray-700" />
        {unread > 0 && (
          <span className="absolute top-1.5 right-1.5 w-2 h-2 bg-red-500 rounded-full" />
        )}
      </button>

      {open && (
        <div className="absolute right-0 mt-2 w-80 bg-white rounded-xl shadow-lg border border-gray-200 z-50 overflow-hidden">
          <div className="flex items-center justify-between px-4 py-2.5 border-b border-gray-100">
            <span className="text-sm font-medium text-gray-900">Notifications</span>
            {unread > 0 && (
              <button
                onClick={() => void markAll()}
                className="text-xs text-gray-500 hover:text-gray-900 inline-flex items-center gap-1"
              >
                <Check className="w-3 h-3" /> Mark all read
              </button>
            )}
          </div>

          <div className="max-h-96 overflow-y-auto overflow-x-hidden">
            {loading && items.length === 0 ? (
              <div className="px-4 py-6 text-center text-sm text-gray-400">Loading…</div>
            ) : items.length === 0 ? (
              <div className="px-4 py-6 text-center text-sm text-gray-400">No notifications</div>
            ) : (
              items.map((n) => (
                <button
                  key={n.id}
                  onClick={() => void openItem(n)}
                  className={`w-full text-left px-4 py-3 border-b border-gray-50 hover:bg-gray-50 transition-colors ${
                    n.read ? '' : 'bg-blue-50/40'
                  }`}
                >
                  <div className="flex items-start gap-2 min-w-0">
                    {!n.read && (
                      <span className="mt-1.5 w-2 h-2 rounded-full bg-blue-500 flex-shrink-0" />
                    )}
                    <div className={`min-w-0 flex-1 ${n.read ? 'pl-4' : ''}`}>
                      <p className="text-sm font-medium text-gray-900 truncate">{n.title}</p>
                      <p className="text-xs text-gray-500 truncate">{n.message}</p>
                    </div>
                  </div>
                </button>
              ))
            )}
          </div>
        </div>
      )}
    </div>
  );
}

export default NotificationBell;
